<template>
  <div
    class="trial-timeline"
    v-if="plan.trialDays > 0">
    <div class="trial-timeline__steps">
      <div class="trial-timeline__step trial-timeline__trial-start">
        <div class="trial-timeline__date">
          {{ $d(trialStartDate, "dayOfMonth") }}
        </div>
        <div class="trial-timeline__dot" />
        <div
          class="trial-timeline__step-info"
          v-html="trialStartCopy" />
      </div>

      <div class="trial-timeline__step trial-timeline__trial-end">
        <div class="trial-timeline__date">
          {{ $d(trialEndDate, "dayOfMonth") }}
        </div>
        <div class="trial-timeline__dot" />
        <div
          class="trial-timeline__step-info"
          v-html="$t('trialEnd')" />
      </div>

      <div class="trial-timeline__bold-axis" />
    </div>

    <div class="trial-timeline__narrow-axis">
      <div class="trial-timeline__axis">
        <div class="trial-timeline__arrow" />
      </div>

      <div
        class="trial-timeline__arrow-message"
        v-html="$t(`arrowMessage.${plan.type}`)" />
    </div>
  </div>
</template>

<script>
const messages = {
  en: {
    daysCount: " | one day | {count} days",
    monthsCount: " | one month | {count} months",
    trialStart: "Start enjoying the entire library of Blinks and shows.",
    trialEnd: "Free trial ends.",
    arrowMessage: {
      monthly: "Continue learning <br>with Blinkist!",
      yearly: "Continue learning <br>with Blinkist!"
    }
  },
  de: {
    daysCount: " | 1 Tag | {count} Tage",
    monthsCount: " | 1 Monat | {count} Monate",
    trialStart: "Du hast jetzt {trialLength} kostenlos Zugang zu allen Titeln.",
    trialEnd: "Ende des Probeabos",
    arrowMessage: {
      monthly: "Dein Monatsabo <br>beginnt.",
      yearly: "Dein Jahresabo <br>beginnt."
    }
  }
};

const dateTimeFormats = {
  en: {
    dayOfMonth: {
      month: "short",
      day: "numeric"
    }
  },
  de: {
    dayOfMonth: {
      month: "long",
      day: "numeric"
    }
  }
};

export default {
  name: "TrialTimeline",
  props: {
    plan: {
      type: Object,
      default: null,
      validator: function (plan) {
        return typeof plan.trialDays === "number" &&
          typeof plan.type === "string";
      }
    }
  },
  data() {
    return { trialStartDate: new Date() };
  },
  computed: {
    trialEndDate() {
      const timestamp = this.trialStartDate;

      timestamp.setDate(timestamp.getDate() + this.plan.trialDays);

      return timestamp;
    },
    trialStartCopy() {
      let trialLength;

      if (this.plan.trialDays % 31 === 0) {
        trialLength = this.$tc("monthsCount", this.plan.trialDays / 31);
      } else {
        trialLength = this.$tc("daysCount", this.plan.trialDays);
      }

      return this.$t("trialStart", { trialLength });
    }
  },
  i18n: {
    dateTimeFormats,
    messages
  }
};
</script>

<style lang="scss">
.trial-timeline {
  @include font-family(sans-serif);

  position: relative;
  z-index: 1;

  display: flex;

  &__step {
    position: relative;
  }

  &__steps {
    position: relative;

    display: flex;
    flex-direction: column;

    @include media(min-width, m) {
      flex-direction: row;
    }
  }

  &__bold-axis {
    position: absolute;
    top: 0;
    left: spacing(10px); /* stylelint-disable-line unit-blacklist */
    z-index: 15;

    width: $spacing-4;
    height: 100%;

    background-color: $blue;

    @include media(min-width, m) {
      top: spacing(10px); /* stylelint-disable-line unit-blacklist */
      left: 0;

      width: 100%;
      height: $spacing-4;
    }
  }

  &__narrow-axis {
    position: absolute;
    top: spacing(156px); /* stylelint-disable-line unit-blacklist */
    left: spacing(11px); /* stylelint-disable-line unit-blacklist */
    z-index: 10;

    display: flex;
    flex-direction: column;

    @include media(min-width, m) {
      top: spacing(-12px); /* stylelint-disable-line unit-blacklist */
      left: 0;

      flex-direction: row;
      align-items: center;
      width: 100%;
      height: auto;
    }
  }

  &__axis {
    position: relative;

    display: flex;
    flex-grow: 1;
    width: spacing(2px);
    height: 108px;

    background-color: $blue5;

    @include media(min-width, m) {
      height: spacing(2px);
    }
  }

  &__arrow {
    position: absolute;
    bottom: spacing(-7px); /* stylelint-disable-line unit-blacklist */
    left: spacing(-3px); /* stylelint-disable-line unit-blacklist */

    width: spacing(6px);
    height: spacing(16px);

    transform: rotate(90deg);

    &:before {
      content: "";

      position: absolute;
      top: spacing(10px); /* stylelint-disable-line unit-blacklist */
      left: spacing(-3px); /* stylelint-disable-line unit-blacklist */

      width: spacing(11px);
      height: spacing(2px);

      background-color: $blue5;

      border-radius: spacing(2px);

      transform: rotate(-60deg);
    }

    &:after {
      content: "";

      position: absolute;
      top: spacing(2px); /* stylelint-disable-line unit-blacklist */
      left: spacing(-3px); /* stylelint-disable-line unit-blacklist */

      width: spacing(11px);
      height: spacing(2px);

      background-color: $blue5;

      border-radius: spacing(2px);

      transform: rotate(60deg);
    }

    @include media(min-width, m) {
      top: spacing(-6px); /* stylelint-disable-line unit-blacklist */
      right: spacing(-1px); /* stylelint-disable-line unit-blacklist */
      bottom: auto;
      left: auto;

      transform: rotate(0deg);
    }
  }

  &__arrow-message {
    @include font-style(p);

    position: absolute;
    bottom: spacing(-8px); /* stylelint-disable-line unit-blacklist */
    left: spacing(-3px); /* stylelint-disable-line unit-blacklist */

    width: spacing(256px);
    margin-left: $spacing-24;

    br { /* stylelint-disable selector-nested-pattern */
      display: none;

      @include media(min-width, m) {
        display: block;
      }
    }

    @include media(min-width, m) {
      position: relative;
      bottom: auto;
      left: auto;

      width: auto;
    }
  }

  &__trial-end {
    margin-top: $spacing-128;

    @include media(min-width, m) {
      margin-top: 0;
      margin-left: spacing(312px); /* stylelint-disable-line unit-blacklist */
    }

    @include media(min-width, l) {
      margin-left: spacing(452px); /* stylelint-disable-line unit-blacklist */
    }
  }

  &__step-info {
    @include font-style(p);

    position: absolute;
    top: $spacing-32;
    left: $spacing-32;

    width: spacing(256px);

    @include media(min-width, m) {
      top: $spacing-32;
      left: 0;

      width: spacing(296px);
    }
  }

  &__date {
    @include font-style(h3, $color: $blue);

    position: absolute;
    top: -$spacing-4;
    left: $spacing-32;

    width: spacing(256px);

    @include media(min-width, m) {
      top: spacing(-40px); /* stylelint-disable-line unit-blacklist */
      left: 0;

      width: spacing(296px);
    }
  }

  &__dot {
    position: relative;
    z-index: 15;

    width: $spacing-24;
    height: $spacing-24;

    background-color: $blue;

    border-radius: 50%;

    @include media(min-width, m) {
      margin-bottom: $spacing-8;
    }
  }
}
</style>
